import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { toast } from "sonner";
import { Loader2, ExternalLink, Sparkles, X, Save, Check, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ModuleRenderer } from "@/pages/ModularPage";
import { getModuleTypeLabel } from "@/components/cms/moduleDefaults";
import PropertyArticleConfigFields from "@/components/cms/PropertyArticleConfigFields";
import MilestonesDashboardModule from "@/components/modules/MilestonesDashboardModule";
import { ColorPickerInput } from "@/components/ui/ColorPickerInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = getApiUrl();

// Detect the editor type for a given config value so the table can render
// the right input. Order matters — boolean must come before number etc.
function detectFieldType(value) {
  if (value === null || value === undefined || value === "") return "text";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (Array.isArray(value)) return "json";
  if (typeof value === "object") return "json";
  // Heuristic for color (#rrggbb or #rgb)
  if (typeof value === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())) {
    return "color";
  }
  // Long strings → textarea
  if (typeof value === "string" && value.length > 80) return "textarea";
  return "text";
}

function humaniseKey(k) {
  return k
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * InstancePreviewModal
 * --------------------
 * Opened from the Module Instances list (eye button, type badge, or name)
 * and shows three sections:
 *   1. AI-generated description (Emergent LLM)
 *   2. "Where it appears" — clickable hyperlinks to the live site
 *   3. Live preview of the instance rendered via the shared ModuleRenderer
 *
 * The AI description is fetched lazily on open (one request per session-open).
 */
export default function InstancePreviewModal({ instance, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [description, setDescription] = useState("");
  const [locations, setLocations] = useState([]);
  // Local editable copy of the instance config — drives the live preview
  // AND the editable fields table. Saved back to the server with debounce.
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  // Master Admin CRUD-on-behalf-of-agent — only relevant for module_type
  // "milestones". Lets the admin pick any agent + their property to manage
  // that property's milestones directly from this modal.
  const [milestoneAgents, setMilestoneAgents] = useState([]);
  const [milestoneAgentsLoading, setMilestoneAgentsLoading] = useState(false);
  const [selectedMilestoneAgentId, setSelectedMilestoneAgentId] = useState("");

  useEffect(() => {
    if (!open || instance?.module_type !== "milestones") return;
    let cancelled = false;
    setMilestoneAgentsLoading(true);
    axios
      .get(`${API}/admin/agents`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const agents = res.data || [];
        setMilestoneAgents(agents);
        setSelectedMilestoneAgentId((prev) =>
          agents.find((a) => a.agent_id === prev) ? prev : (agents[0]?.agent_id || "")
        );
      })
      .catch(() => setMilestoneAgents([]))
      .finally(() => { if (!cancelled) setMilestoneAgentsLoading(false); });
    return () => { cancelled = true; };
  }, [open, instance?.module_type]);

  const selectedMilestoneAgent = milestoneAgents.find((a) => a.agent_id === selectedMilestoneAgentId);

  useEffect(() => {
    if (!open || !instance?.instance_id) {
      setConfig({});
      return;
    }
    setConfig(instance.config || {});
    setSavedAt(null);
  }, [open, instance?.instance_id, instance?.config]);

  useEffect(() => {
    if (!open || !instance?.instance_id) return;
    let cancelled = false;

    // Seed from any persisted description on the instance so the modal is
    // never blank for a known module. The backend's GET also returns the
    // cached value when present (we still hit it once to populate locations
    // and to lazily generate when the saved description is empty).
    const cached = (instance.description || "").trim();
    if (cached) {
      setDescription(cached);
    } else {
      setDescription("");
    }
    setLocations([]);
    setLoading(true);
    axios
      .post(
        `${API}/admin/module-instances/${instance.instance_id}/describe`,
        {},
        { withCredentials: true }
      )
      .then((r) => {
        if (cancelled) return;
        setDescription(r.data?.description || "");
        setLocations(r.data?.locations || []);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        toast.error("Failed to load instance description");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, instance?.instance_id, instance?.description]);

  // Sort fields alphabetically — declared BEFORE any early return to honour
  // the rules of hooks. Falls back to {} when no instance is loaded.
  const fieldEntries = useMemo(() => {
    const entries = Object.entries(config || {});
    entries.sort(([a], [b]) => a.localeCompare(b));
    return entries;
  }, [config]);

  if (!instance) return null;

  // Debounced auto-save: every edit shifts the timer; 600 ms after the last
  // edit the patched config is PUT back to the server.
  const scheduleSave = (nextConfig) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await axios.put(
          `${API}/admin/module-instances/${instance.instance_id}`,
          { config: nextConfig },
          { withCredentials: true }
        );
        setSavedAt(Date.now());
        if (onSaved) onSaved();
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.detail || "Auto-save failed");
      } finally {
        setSaving(false);
      }
    }, 600);
  };

  const handleFieldChange = (key, value) => {
    const next = { ...config, [key]: value };
    setConfig(next);
    scheduleSave(next);
  };

  // Force-regenerate the AI description for this instance. Backend persists
  // the new text on the module_instances doc and returns it.
  const handleRegenerateDescription = async () => {
    if (!instance?.instance_id) return;
    try {
      setRegenerating(true);
      const r = await axios.post(
        `${API}/admin/module-instances/${instance.instance_id}/describe?force=true`,
        {},
        { withCredentials: true }
      );
      setDescription(r.data?.description || "");
      setLocations(r.data?.locations || []);
      toast.success("Description regenerated");
      if (onSaved) onSaved();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to regenerate description");
    } finally {
      setRegenerating(false);
    }
  };

  const liveOrigin =
    typeof window !== "undefined" ? window.location.origin : "";

  // ModuleRenderer expects `{ module, moduleDetail }` shapes. We supply the
  // instance's config as the canonical `moduleDetail` and pass an empty
  // override module so the preview matches what the public site renders.
  const moduleForRenderer = { config_override: {} };
  const moduleDetailForRenderer = {
    type: instance.module_type,
    config: config || {},
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="instance-preview-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span>{instance.name}</span>
                <Badge variant="outline">
                  {getModuleTypeLabel(instance.module_type)}
                </Badge>
                {instance.enabled === false && (
                  <Badge variant="secondary">Disabled</Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                AI-generated summary, live locations, and a real-time preview
                of how this instance renders with the current Site Styles.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* AI description */}
        <section
          className="rounded-md border border-purple-200 bg-purple-50/50 p-4 space-y-2"
          data-testid="instance-preview-description"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
              <Sparkles className="h-4 w-4" />
              What this is &amp; where it lives
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRegenerateDescription}
              disabled={loading || regenerating}
              data-testid="regenerate-description-btn"
              title="Re-run the AI and replace the saved description"
            >
              {regenerating ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Regenerate
            </Button>
          </div>
          {loading || regenerating ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {regenerating ? "Re-generating with AI…" : "Generating description…"}
            </div>
          ) : (
            <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">
              {description ||
                "No description available — try again or check the AI key."}
            </p>
          )}
        </section>

        {/* Locations */}
        <section
          className="rounded-md border border-border bg-muted/30 p-4 space-y-2"
          data-testid="instance-preview-locations"
        >
          <div className="text-sm font-semibold">Where it appears</div>
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading locations…</p>
          ) : locations.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Not currently attached to any page. Attach it from CMS → Modular
              Pages or CMS → Page Overlays.
            </p>
          ) : (
            <ul className="space-y-1">
              {locations.map((loc, i) => (
                <li
                  key={`${loc.path}-${loc.source}-${i}`}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="text-muted-foreground">
                    {loc.title} <span className="text-xs">({loc.source})</span>
                  </span>
                  <a
                    href={loc.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline font-mono text-xs"
                    data-testid={`instance-preview-link-${i}`}
                  >
                    {liveOrigin}
                    {loc.path}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Live preview */}
        <section
          className="rounded-md border border-border overflow-hidden bg-background"
          data-testid="instance-preview-render"
        >
          <div className="px-4 py-2 border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            Live preview
          </div>
          <div
            data-public-site="true"
            className="max-h-[60vh] overflow-y-auto"
          >
            <ModuleRenderer
              module={moduleForRenderer}
              moduleDetail={moduleDetailForRenderer}
            />
          </div>
        </section>

        {/* Manage milestones — Master Admin CRUD on behalf of any agent.
            Only rendered for module_type "milestones"; reuses the exact
            same CRUD UI shoppers/agents see live, scoped via agent_id. */}
        {instance.module_type === "milestones" && (
          <section
            className="rounded-md border border-border overflow-hidden bg-background"
            data-testid="instance-preview-milestones-admin"
          >
            <div className="px-4 py-2 border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              Manage milestones (as agent)
            </div>
            <div className="p-4 space-y-4">
              <div className="max-w-sm">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Agent</label>
                <Select
                  value={selectedMilestoneAgentId}
                  onValueChange={setSelectedMilestoneAgentId}
                  disabled={milestoneAgentsLoading || milestoneAgents.length === 0}
                >
                  <SelectTrigger className="mt-1" data-testid="milestones-admin-agent-select">
                    <SelectValue placeholder={milestoneAgentsLoading ? "Loading agents…" : "Choose an agent"} />
                  </SelectTrigger>
                  <SelectContent>
                    {milestoneAgents.map((a) => (
                      <SelectItem key={a.agent_id} value={a.agent_id} data-testid={`milestones-admin-agent-option-${a.agent_id}`}>
                        {a.name || a.email} ({a.properties_count} propert{a.properties_count === 1 ? "y" : "ies"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!milestoneAgentsLoading && milestoneAgents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No agents found.</p>
              ) : selectedMilestoneAgentId ? (
                <div className="border-t border-border pt-4" data-public-site="true">
                  <MilestonesDashboardModule
                    config={config || {}}
                    adminAgentId={selectedMilestoneAgentId}
                    adminProperties={selectedMilestoneAgent?.linked_properties || []}
                  />
                </div>
              ) : null}
            </div>
          </section>
        )}

        {/* Editable fields — special-cased for property_article so admins
            get the proper themes/scheduler/CRUD UI instead of a generic
            "one row per config key" table. */}
        {instance.module_type === "property_article" ? (
          <section
            className="rounded-md border border-border bg-background p-4"
            data-testid="instance-preview-property-article-fields"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Property Article settings
              </span>
              <span
                className="text-xs flex items-center gap-1"
                data-testid="instance-preview-autosave-status"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground">Saving…</span>
                  </>
                ) : savedAt ? (
                  <>
                    <Check className="h-3 w-3 text-green-600" />
                    <span className="text-green-700">Saved</span>
                  </>
                ) : (
                  <>
                    <Save className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Auto-save on edit</span>
                  </>
                )}
              </span>
            </div>
            <PropertyArticleConfigFields
              config={config || {}}
              onChange={(updater) => {
                const next = typeof updater === "function" ? updater(config || {}) : updater;
                setConfig(next);
                scheduleSave(next);
              }}
            />
          </section>
        ) : (
        <section
          className="rounded-md border border-border overflow-hidden bg-background"
          data-testid="instance-preview-fields-table"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/40">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Configuration fields ({fieldEntries.length})
            </span>
            <span
              className="text-xs flex items-center gap-1"
              data-testid="instance-preview-autosave-status"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Saving…</span>
                </>
              ) : savedAt ? (
                <>
                  <Check className="h-3 w-3 text-green-600" />
                  <span className="text-green-700">Saved</span>
                </>
              ) : (
                <>
                  <Save className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Auto-save on edit</span>
                </>
              )}
            </span>
          </div>
          {fieldEntries.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">
              This instance has no configuration fields yet.
            </p>
          ) : (
            <div className="max-h-[40vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-muted/60 z-10">
                  <TableRow>
                    <TableHead className="w-1/3">Field</TableHead>
                    <TableHead className="w-24">Type</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fieldEntries.map(([key, value]) => {
                    const type = detectFieldType(value);
                    return (
                      <TableRow key={key} data-testid={`field-row-${key}`}>
                        <TableCell className="font-mono text-xs align-top">
                          <div className="font-medium text-sm" title={key}>
                            {humaniseKey(key)}
                          </div>
                          <div className="text-muted-foreground text-[10px]">
                            {key}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge variant="outline" className="text-[10px]">
                            {type}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-top">
                          {type === "boolean" && (
                            <input
                              type="checkbox"
                              checked={!!value}
                              onChange={(e) => handleFieldChange(key, e.target.checked)}
                              data-testid={`field-input-${key}`}
                            />
                          )}
                          {type === "number" && (
                            <Input
                              type="number"
                              value={value ?? ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  key,
                                  e.target.value === "" ? "" : Number(e.target.value)
                                )
                              }
                              className="h-8"
                              data-testid={`field-input-${key}`}
                            />
                          )}
                          {type === "color" && (
                            <ColorPickerInput
                              value={value}
                              onChange={(v) => handleFieldChange(key, v)}
                              compact
                              testId={`field-input-${key}`}
                            />
                          )}
                          {type === "textarea" && (
                            <textarea
                              value={value || ""}
                              onChange={(e) => handleFieldChange(key, e.target.value)}
                              rows={3}
                              className="w-full text-sm rounded-md border border-input bg-background px-2 py-1 font-mono"
                              data-testid={`field-input-${key}`}
                            />
                          )}
                          {type === "text" && (
                            <Input
                              type="text"
                              value={value ?? ""}
                              onChange={(e) => handleFieldChange(key, e.target.value)}
                              className="h-8 text-sm"
                              data-testid={`field-input-${key}`}
                            />
                          )}
                          {type === "json" && (
                            <textarea
                              value={(() => {
                                try { return JSON.stringify(value, null, 2); }
                                catch { return String(value); }
                              })()}
                              onChange={(e) => {
                                // Only commit a valid JSON parse; otherwise keep the
                                // editor open without breaking the live preview.
                                const raw = e.target.value;
                                try {
                                  const parsed = JSON.parse(raw);
                                  handleFieldChange(key, parsed);
                                } catch {
                                  // Track the in-progress text as a string draft
                                  // so the textarea stays controlled. Revert to
                                  // the parsed value on blur if invalid.
                                  setConfig((prev) => ({ ...prev, [key]: raw }));
                                }
                              }}
                              rows={4}
                              className="w-full text-xs rounded-md border border-input bg-background px-2 py-1 font-mono"
                              data-testid={`field-input-${key}`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
        )}

        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="instance-preview-close"
          >
            <X className="h-4 w-4 mr-1" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
